import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { StageBadge, FuBadge, QuickActions, FollowUpModal, fmtINR } from "./CrmShared";

const VIEWS = [
  { k: "overdue", label: "Overdue", cls: "border-red-200 bg-red-50/40", title: "text-red-600" },
  { k: "today", label: "Today", cls: "border-amber-200 bg-amber-50/40", title: "text-amber-600" },
  { k: "upcoming", label: "Upcoming", cls: "border-border bg-background", title: "" },
];

export default function CrmFollowUps({ refreshKey }) {
  const [data, setData] = useState({ overdue: [], today: [], upcoming: [] });
  const [fuLead, setFuLead] = useState(null);

  const load = useCallback(async () => {
    try {
      const [o, t, u] = await Promise.all(["overdue", "today", "upcoming"].map((v) => api.get("/store-admin/crm/followups", { params: { view: v } })));
      setData({ overdue: o.data.followups, today: t.data.followups, upcoming: u.data.followups });
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const complete = async (l) => {
    try { await api.post(`/store-admin/crm/leads/${l.id}/followup/complete`); toast.success("Follow-up completed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="crm-followups">
      {VIEWS.map((v) => (
        <div key={v.k} className={`rounded-2xl border p-5 ${v.cls}`} data-testid={`fu-section-${v.k}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={`font-display font-bold ${v.title}`}>{v.label} Follow-ups</h3>
            <span className="text-xs font-extrabold text-muted-foreground">{data[v.k].length}</span>
          </div>
          {data[v.k].length === 0 ? <p className="text-sm text-muted-foreground py-2">No {v.label.toLowerCase()} follow-ups.</p> : (
            <div className="space-y-2">
              {data[v.k].map((l) => (
                <div key={l.id} className="rounded-xl border border-border bg-background p-4 flex flex-wrap items-center gap-3" data-testid={`fu-row-${l.id}`}>
                  <div className="flex-1 min-w-[160px]">
                    <p className="font-bold text-sm truncate">{l.name}{l.company ? ` · ${l.company}` : ""}</p>
                    {l.follow_up_note && <p className="text-xs text-muted-foreground mt-0.5 break-words">{l.follow_up_note}</p>}
                    <div className="flex flex-wrap items-center gap-2 mt-1.5"><StageBadge stage={l.stage} /><FuBadge lead={l} /></div>
                  </div>
                  {l.value > 0 && <span className="text-sm font-extrabold">{fmtINR(l.value)}</span>}
                  <QuickActions name={l.name} phone={l.phone} whatsapp={l.whatsapp} email={l.email} />
                  <div className="flex gap-1.5">
                    <button onClick={() => complete(l)} data-testid={`fu-complete-${l.id}`} title="Mark completed"
                      className="h-8 w-8 rounded-lg border border-border flex items-center justify-center text-emerald-600 hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4" /></button>
                    <button onClick={() => setFuLead(l)} data-testid={`fu-resched-${l.id}`} title="Reschedule"
                      className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted"><CalendarClock className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <FollowUpModal open={!!fuLead} onClose={() => setFuLead(null)} lead={fuLead} onSaved={load} />
    </div>
  );
}
